import Alert from '../../common/components/Alert';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import Grid from '@material-ui/core/Grid';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import React, { Suspense } from 'react';
import useHiddenHeaderFooter from '../hooks/useHiddenHeaderFooter';
import { feedbackTypes, surveyTypes } from '../constants/shl360';
import { FullPageLoader } from '../../common/components/FullPageLoader';
import { getCookie, setCookie } from '../../common/utils/cookies';
import { getRouteMfsTaskListPage } from '../utils/helper';
import { LayoutStyles } from '../styles/mfsTheme';
import { LayoutStyles as HideFooter } from '../styles/common';
import { MFSPlayerProps } from '../interfaces/MFSPlayer';
import { RaterStyles } from '../styles/rater';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../redux-store/hook';
const RatingQuestion = React.lazy(() => import('./layouts/RatingQuestionScreen'));
const OpenQuestion = React.lazy(() => import('./layouts/OpenQuestion'));
const MCQQuestion = React.lazy(() => import('./layouts/McqQuestionScreen'));

/* Tips and Intro Screens */
const TipsScreen = React.lazy(() => import('./instructions/TipsScreen'));
const InstructionScreen = React.lazy(() => import('./instructions/InstructionsScreen'));
/* Tips and Intro Screens */

const MFSPlayer = ({
  questionData,
  moveToNextQuestion,
  moveToPrevQuestion,
  moveWithoutProgress,
  updateSelectedResponse,
  setShowViewAll,
  currentQusIndex,
  tips,
  progress,
  surveyMeta,
  isError,
  navigationType,
  isPreview,
}: MFSPlayerProps): JSX.Element => {
  const classes = LayoutStyles();
  const alertStyle = RaterStyles();
  const customClass = HideFooter();
  const type = questionData?.type || '';
  const data = questionData?.data || '';
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const currentWorkflowItem = useAppSelector((state) => state.currentWorkflowItem);

  useHiddenHeaderFooter(`${classes.container} ${!isPreview ? '' : classes.previewContainer}`);

  const redirectToMfsTaskListPage = (): void => {
    history.push(getRouteMfsTaskListPage(currentWorkflowItem));
  };

  React.useEffect(() => {
    // Set cookie in case of preview and reload page to apply translations
    if (isPreview && surveyMeta?.language && getCookie('locale_player') !== surveyMeta?.language) {
      setCookie('locale_player', surveyMeta?.language);
      setCookie('oldlang', surveyMeta?.language);
      i18n.changeLanguage(surveyMeta?.language.toLowerCase());
    }
    document.title = t('shl360');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyMeta?.language]);

  if (
    !isError &&
    isPreview &&
    (getCookie('locale_player') === null || getCookie('locale_player') !== surveyMeta?.language)
  ) {
    return <FullPageLoader isLoading={true} customClass={customClass.screenContainer} />;
  }

  return (
    <>
      <Suspense fallback={<FullPageLoader isLoading={true} customClass={customClass.screenContainer} />}>
        {questionData && isError && Object.keys(questionData).length === 0 ? (
          <Grid container justify="center" className={classes.containerWrap}>
            <Alert show={true} state="error" gridClassName={alertStyle.errorMsgCenter}>
              {t('administratorGenericError')}
            </Alert>
            {!isPreview && (
              <Grid xs={12} item>
                <Box display="flex" mt={3} mb={6}>
                  <Grid xs item className={alertStyle.textAlignLeft}>
                    <Button
                      variant="contained"
                      className={clsx(alertStyle.doneButton, alertStyle.width100)}
                      onClick={redirectToMfsTaskListPage}
                      aria-label={t('back')}>
                      <NavigateBeforeIcon className={classes.backArrowIcon} /> {t('back')}
                    </Button>
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        ) : (
          <>
            {type === surveyTypes.TIPS && (
              <TipsScreen tips={tips} isPreview={isPreview} onInstructionNext={moveToNextQuestion} />
            )}
            {type === surveyTypes.INTRO && data === surveyTypes.QU && (
              <InstructionScreen
                onInstructionNext={moveToNextQuestion}
                onInstructionBack={moveToPrevQuestion}
                variant={'behaviour'}
                taskVariant={surveyMeta?.raterCategory === feedbackTypes.SELF ? 'self' : 'others'}
              />
            )}
            {type === surveyTypes.INTRO && data === surveyTypes.IR && (
              <InstructionScreen
                onInstructionNext={moveToNextQuestion}
                onInstructionBack={moveToPrevQuestion}
                variant={'importance'}
                taskVariant={surveyMeta?.raterCategory === feedbackTypes.SELF ? 'self' : 'others'}
              />
            )}
            {type === surveyTypes.INTRO && data === surveyTypes.AD && (
              <InstructionScreen
                onInstructionNext={moveToNextQuestion}
                onInstructionBack={moveToPrevQuestion}
                variant={'additional'}
                taskVariant={surveyMeta?.raterCategory === feedbackTypes.SELF ? 'self' : 'others'}
              />
            )}
            {(type === surveyTypes.QU || type === surveyTypes.IR) && (
              <RatingQuestion
                {...questionData}
                moveToNextQuestion={moveToNextQuestion}
                moveToPrevQuestion={moveToPrevQuestion}
                updateSelectedResponse={updateSelectedResponse}
                moveWithoutProgress={moveWithoutProgress}
                setShowViewAll={setShowViewAll}
                currentQusIndex={currentQusIndex}
                surveyMeta={surveyMeta}
                progress={progress}
                isError={isError}
                navigationType={navigationType}
                isPreview={isPreview}
              />
            )}
            {type === surveyTypes.OQ && (
              <OpenQuestion
                {...questionData}
                moveToNextQuestion={moveToNextQuestion}
                moveToPrevQuestion={moveToPrevQuestion}
                updateSelectedResponse={updateSelectedResponse}
                moveWithoutProgress={moveWithoutProgress}
                setShowViewAll={setShowViewAll}
                currentQusIndex={currentQusIndex}
                surveyMeta={surveyMeta}
                progress={progress}
                isError={isError}
                navigationType={navigationType}
              />
            )}
            {type === surveyTypes.MC && (
              <MCQQuestion
                {...questionData}
                moveToNextQuestion={moveToNextQuestion}
                moveToPrevQuestion={moveToPrevQuestion}
                updateSelectedResponse={updateSelectedResponse}
                moveWithoutProgress={moveWithoutProgress}
                setShowViewAll={setShowViewAll}
                currentQusIndex={currentQusIndex}
                surveyMeta={surveyMeta}
                progress={progress}
                isError={isError}
                navigationType={navigationType}
              />
            )}
          </>
        )}
      </Suspense>
    </>
  );
};

export default MFSPlayer;
